/**
 * Manager - Staff Activity. Lists firm employees with their activity counts,
 * and drills into one employee's feed of what they do in RoniDoc: AI
 * searches/chats, client communications, and documents they generated - each
 * with a date/time. Backed by GET /manager/staff-activity[?userId=].
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Users, Search, MessageSquare, Send, FileText, Mail, Upload, StickyNote,
  Clock, Shield, RefreshCw, Activity, Loader2, ChevronRight, ChevronLeft,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import apiClient from '@/services/api/ma-client';

const KIND_CONFIG: Record<string, { icon: any; bg: string; text: string; verb: string }> = {
  search: { icon: Search, bg: 'bg-sky-500/15', text: 'text-sky-500', verb: 'Searched' },
  chat: { icon: MessageSquare, bg: 'bg-violet-500/15', text: 'text-violet-500', verb: 'Asked AI' },
  client_message: { icon: Send, bg: 'bg-blue-500/15', text: 'text-blue-500', verb: 'Messaged client' },
  document_generated: { icon: FileText, bg: 'bg-indigo-500/15', text: 'text-indigo-500', verb: 'Generated' },
  email_sent: { icon: Mail, bg: 'bg-green-500/15', text: 'text-green-500', verb: 'Sent email' },
  document_uploaded: { icon: Upload, bg: 'bg-slate-500/15', text: 'text-slate-500', verb: 'Uploaded' },
  note: { icon: StickyNote, bg: 'bg-amber-500/15', text: 'text-amber-500', verb: 'Noted' },
  deadline: { icon: Clock, bg: 'bg-red-500/15', text: 'text-red-500', verb: 'Deadline' },
  compliance_check: { icon: Shield, bg: 'bg-emerald-500/15', text: 'text-emerald-500', verb: 'Checked' },
  xero_sync: { icon: RefreshCw, bg: 'bg-sky-500/15', text: 'text-sky-500', verb: 'Synced Xero' },
};
const kindCfg = (k: string) => KIND_CONFIG[k] || { icon: Activity, bg: 'bg-muted', text: 'text-muted-foreground', verb: k };

function relativeTime(iso?: string): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (isNaN(then)) return '';
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

const fullName = (s: any) => `${s.first_name || ''} ${s.last_name || ''}`.trim() || s.email || 'Unknown';

export default function ManagerStaffPage() {
  const [selected, setSelected] = useState<any | null>(null);

  const { data: listData, isLoading } = useQuery({
    queryKey: ['ma-staff'],
    queryFn: async () => { const r = await apiClient.get('/manager/staff-activity'); return r.data.data; },
  });

  const { data: feedData, isLoading: feedLoading } = useQuery({
    enabled: !!selected,
    queryKey: ['ma-staff-feed', selected?.user_id],
    queryFn: async () => { const r = await apiClient.get(`/manager/staff-activity?userId=${selected.user_id}&limit=150`); return r.data.data; },
  });

  const staff = listData?.staff || [];

  // ---- Employee detail (activity feed) ----
  if (selected) {
    const events: any[] = feedData?.events || [];
    return (
      <div className="px-4 py-6 max-w-lg mx-auto space-y-4">
        <button onClick={() => setSelected(null)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ChevronLeft className="h-4 w-4" /> All staff
        </button>
        <div>
          <h2 className="text-lg font-medium text-foreground">{fullName(selected)}</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">{selected.email}{selected.role ? ` - ${String(selected.role).replace(/_/g, ' ')}` : ''}</p>
        </div>

        {feedLoading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-[var(--v4-accent)]" /></div>
        ) : events.length === 0 ? (
          <div className="rounded-xl border border-border bg-card p-8 text-center">
            <Activity className="mx-auto h-8 w-8 text-muted-foreground/50" />
            <p className="mt-3 text-sm font-medium text-foreground">No recorded activity</p>
            <p className="mt-1 text-xs text-muted-foreground">This person's searches, messages and generated documents will show here.</p>
          </div>
        ) : (
          <div className="relative space-y-1">
            <div className="absolute left-[18px] top-2 bottom-2 w-px bg-border" aria-hidden />
            {events.map((e: any, i: number) => {
              const { icon: Icon, bg, text, verb } = kindCfg(e.kind);
              return (
                <div key={i} className="relative flex gap-3 rounded-xl p-2">
                  <div className={cn('relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ring-4 ring-background', bg)}>
                    <Icon className={cn('h-4 w-4', text)} />
                  </div>
                  <div className="min-w-0 flex-1 pt-0.5">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm text-foreground">
                        <span className="font-medium">{verb}</span>
                        {e.client_name && <span className="text-muted-foreground"> - {e.client_name}</span>}
                      </p>
                      <span className="shrink-0 text-[11px] text-muted-foreground">{relativeTime(e.ts)}</span>
                    </div>
                    {e.label && <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">{String(e.label).replace(/[—–―‒]/g, '-')}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ---- Staff list ----
  return (
    <div className="px-4 py-6 max-w-lg mx-auto space-y-4">
      <div>
        <h2 className="text-lg font-medium text-foreground">Staff activity</h2>
        <p className="mt-0.5 flex items-center gap-1 text-sm text-muted-foreground"><Users className="h-3.5 w-3.5" /> What your team does in RoniDoc</p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-[var(--v4-accent)]" /></div>
      ) : staff.length === 0 ? (
        <div className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">No staff found.</div>
      ) : (
        <div className="space-y-2">
          {staff.map((s: any) => {
            const initials = (fullName(s).split(' ').map((p: string) => p[0]).join('').slice(0, 2) || '?').toUpperCase();
            return (
              <button
                key={s.user_id}
                onClick={() => setSelected(s)}
                className="flex w-full items-center gap-3 rounded-xl border border-border bg-card p-3 text-left transition hover:bg-muted/50"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--v4-accent)]/15 text-sm font-semibold text-[var(--v4-accent)]">
                  {initials}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">{fullName(s)}</p>
                  <p className="text-xs text-muted-foreground">
                    {s.activity_count > 0
                      ? `${s.activity_count} action${s.activity_count === 1 ? '' : 's'} - last ${relativeTime(s.last_active)}`
                      : 'No activity yet'}
                  </p>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
